import { TFunction } from 'next-i18next'
import { DropdownItems, FieldType, Schema } from '@afterpaytouch/form'
import { GetSchemaAndFormDataResult, EmploymentDetailsForm } from './types'
import { requiredValidation } from './validations'
import { getDropdownFieldSchema } from '../VerifyIdentity/utils'
import { EmploymentStatus } from '@afterpaytouch/portal-api'
import { LogEvent, TrackingEvent } from '../../model/monitoring'
import { Money } from '@afterpay/types'

const calculateMaximumCreditFacilityLimit = (facilityLimitAmount: string, orderAmount: string, formatMoney: (amount?: string) => string): DropdownItems[] => {
  const dropdownIncrement = 1000
  const lowValue = Number(orderAmount)
  const highValue = Number(facilityLimitAmount)

  if (isNaN(lowValue) || isNaN(highValue)) {
    return []
  }

  const highSteps = Math.ceil(highValue / dropdownIncrement)
  const lowSteps = Math.floor(lowValue / dropdownIncrement)
  const noOfSteps = highSteps - lowSteps

  const dropdown: DropdownItems[] = Array.from({ length: noOfSteps }, (_, index) => {
    const incrementAmount = String((index + 1 + lowSteps) * dropdownIncrement)
    return {
      key: incrementAmount,
      label: formatMoney(incrementAmount),
    }
  })

  // Add the original amount to the bottom of the dropdown list
  const dropdownWithOriginalAmount: DropdownItems[] = [
    {
      key: orderAmount ?? '',
      label: formatMoney(orderAmount),
    },
  ]
    .concat(dropdown)
    .reverse()

  return dropdownWithOriginalAmount
}

export const generateCreditLimitList = (
  orderAmount: Money | undefined,
  facilityLimit: Money | undefined,
  formatMoney: (amount?: string) => string
): DropdownItems[] => {
  if (typeof orderAmount === 'undefined' || typeof facilityLimit === 'undefined') {
    return []
  }

  return calculateMaximumCreditFacilityLimit(facilityLimit?.amount, orderAmount?.amount, formatMoney)
}

interface EmploymentDetailsComponents {
  creditLimitText: () => JSX.Element
  upcomingExpensesText: () => JSX.Element
  footer: () => JSX.Element
}

export const getSchema = (
  components: EmploymentDetailsComponents,
  t: TFunction,
  logEvent: LogEvent,
  creditLimitList: DropdownItems[]
): GetSchemaAndFormDataResult<EmploymentDetailsForm> => {
  const required = requiredValidation(t)

  const schema: Schema<EmploymentDetailsForm> = {
    maxFacilityLimit: getDropdownFieldSchema({
      id: 'credit-limit-dropdown-field',
      label: t('consumerLending:employmentDetails:formLabels:creditLimit:label'),
      value: creditLimitList[0]?.key,
      otherProps: {
        onSelect: (selectedValue) => logEvent(TrackingEvent.PRESSED_CL_CREDIT_EMPLOYMENT_DETAILS_PAGE_MAXIMUM_LIMIT_DROPDOWN, { selectedValue }),
        'data-dd-action-name': 'Employment Details Credit Limit Dropdown',
      },
      options: {
        ...required('creditLimit'),
      },
      items: creditLimitList,
    }),
    creditLimitText: {
      type: FieldType.Component,
      Component: components.creditLimitText,
    },
    employmentStatus: getDropdownFieldSchema({
      id: 'employment-status-dropdown-field',
      label: t('consumerLending:employmentDetails:formLabels:employmentStatus:label'),
      otherProps: {
        onSelect: (selectedValue) => logEvent(TrackingEvent.PRESSED_CL_CREDIT_EMPLOYMENT_DETAILS_PAGE_EMPLOYMENT_STATUS_DROPDOWN, { selectedValue }),
        'data-dd-action-name': 'Employment Details Employment Status Dropdown',
      },
      options: {
        ...required('employmentStatus'),
      },
      items: [
        { key: EmploymentStatus.FullTime, label: t('consumerLending:employmentDetails:formLabels:employmentStatus:fullTime') },
        { key: EmploymentStatus.PartTime, label: t('consumerLending:employmentDetails:formLabels:employmentStatus:partTime') },
        { key: EmploymentStatus.SelfEmployed, label: t('consumerLending:employmentDetails:formLabels:employmentStatus:selfEmployed') },
        { key: EmploymentStatus.Casual, label: t('consumerLending:employmentDetails:formLabels:employmentStatus:casual') },
        { key: EmploymentStatus.NotEmployed, label: t('consumerLending:employmentDetails:formLabels:employmentStatus:notEmployed') },
      ],
    }),
    upcomingExpensesText: {
      type: FieldType.Component,
      Component: components.upcomingExpensesText,
    },
    changeInCircumstances: getDropdownFieldSchema({
      id: 'upcoming-expenses-dropdown-field',
      label: t('consumerLending:employmentDetails:formLabels:upcomingExpenses:label'),
      otherProps: {
        onSelect: (selectedValue) => logEvent(TrackingEvent.PRESSED_CL_CREDIT_EMPLOYMENT_DETAILS_PAGE_FORESEEABLE_ISSUES_DROPDOWN, { selectedValue }),
        'data-dd-action-name': 'Employment Details Change In Circumstances Dropdown',
      },
      options: {
        ...required('upcomingExpenses'),
      },
      items: [
        { key: 'true', label: t('consumerLending:employmentDetails:formLabels:upcomingExpenses:yes') },
        { key: 'false', label: t('consumerLending:employmentDetails:formLabels:upcomingExpenses:no') },
      ],
    }),
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }
  return { schema }
}
